import { env } from '@/config/env'
import { useUsageDataStore } from '@/features/account/usage-data.store'
import { useFlagsStore } from '@/features/flags/flags.store'
import { setFirstPartySink } from '@/lib/analytics'
import { timezoneHeaders } from '@/lib/api'
import { clientHeaders } from '@/lib/client-info'
import { deviceId } from '@/lib/device-id'
import { createEventQueue, sendWithKeepalive, type EventQueue, type QueueReadiness, type QueuedEvent } from '@/lib/event-queue'
import { http } from '@/lib/http'
import { safeStorage } from '@/lib/storage'
import { useSessionStore } from '@/stores/session.store'

/** Off by default on the backend; the web app stops sending to `/v2/events` as soon as it learns it is off (LP-947). */
export const ACTIVITY_FLAG = 'activity_tracking'
export const EVENTS_PATH = '/v2/events'

/** The last answer we had on the flag, kept per browser: a signed-out client cannot read `/feature-flags`. */
export const ACTIVITY_ENABLED_KEY = 'leazepro.activityEnabled'

function lastKnown(): boolean | null {
  const v = safeStorage.getItem(ACTIVITY_ENABLED_KEY)
  return v === 'true' ? true : v === 'false' ? false : null
}

function remember(enabled: boolean): void {
  if (lastKnown() !== enabled) safeStorage.setItem(ACTIVITY_ENABLED_KEY, String(enabled))
}

/**
 * The gate of the LP-938 contract addendum. The opt-out always wins. Signed in with the flags loaded: the flag.
 * Otherwise (signed out, booting): the last known value — from a previous sign-in or the last `/v2/events`
 * answer — and when there is none, send once and follow the answer's `enabled`.
 */
export function activityReadiness(): QueueReadiness {
  if (!useUsageDataStore.getState().share) return false
  const { loaded, flags } = useFlagsStore.getState()
  if (loaded) return flags[ACTIVITY_FLAG] === 'enabled'
  return lastKnown() ?? true
}

function hiddenTabHeaders(): Record<string, string> {
  const token = useSessionStore.getState().accessToken
  return { ...timezoneHeaders(), ...clientHeaders(), ...(token ? { Authorization: `Bearer ${token}` } : {}) }
}

let queue: EventQueue | null = null

/** Wires the first-party sink once, at start-up (`App.tsx`), before the first screen view. */
export function startActivityTracking(): EventQueue {
  if (queue) return queue
  const q = createEventQueue({
    send: async (events: QueuedEvent[]) => {
      const result = await http.post<{ accepted: number; dropped: number; enabled?: boolean }>(EVENTS_PATH, { events })
      // While the flag is off the backend stores nothing and says so: stop until we learn otherwise.
      if (result?.enabled === false) {
        remember(false)
        q.clear()
      } else if (result?.enabled === true) remember(true)
      return result
    },
    sendOnHide: (events) => sendWithKeepalive(`${env.apiBaseUrl}${EVENTS_PATH}`, hiddenTabHeaders(), events),
    ready: activityReadiness,
  })
  setFirstPartySink({
    allowed: () => useUsageDataStore.getState().share,
    push: q.push,
    role: () => useSessionStore.getState().user?.defaultProfile,
    anonymousId: () => (useSessionStore.getState().accessToken ? undefined : deviceId()),
  })
  // Turning "Share usage data" off forgets what was waiting; the flags loading is remembered for the next signed-out visit.
  useUsageDataStore.subscribe((state) => {
    if (!state.share) q.clear()
  })
  useFlagsStore.subscribe((state) => {
    if (state.loaded) remember(state.flags[ACTIVITY_FLAG] === 'enabled')
    void q.flush()
  })
  q.start()
  queue = q
  return q
}
